// bm-design-system: photo-file-input primitive
import * as React from "react";
import { Camera, ChevronDown, FolderOpen, Image, ImagePlus } from "lucide-react";
import { Button, type ButtonProps } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const IMAGE_EXTENSIONS = /\.(jpe?g|png|gif|webp|avif|heic|heif|bmp|tiff?|svg)$/i;

function isImageFile(file: File) {
  // HEIC/HEIF often arrive with an empty MIME type, so fall back to the extension.
  return file.type ? file.type.startsWith("image/") : IMAGE_EXTENSIONS.test(file.name);
}

export interface PhotoFileInputProps {
  onSelect: (files: File[]) => void;
  multiple?: boolean;
  label?: React.ReactNode;
  id?: string;
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  className?: string;
}

// One button, three photo sources. A plain accept="image/*" input on modern
// Android opens only the OS photo picker — no camera, no file browser — so
// each source gets its own hidden input: capture="environment" forces the
// camera, accept="image/*" the photo picker, and no accept the file browser
// (filtered back down to images after selection).
export function PhotoFileInput({
  onSelect,
  multiple = false,
  label,
  id,
  variant = "secondary",
  size = "sm",
  className,
}: PhotoFileInputProps) {
  const cameraRef = React.useRef<HTMLInputElement>(null);
  const photosRef = React.useRef<HTMLInputElement>(null);
  const filesRef = React.useRef<HTMLInputElement>(null);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []).filter(isImageFile);
    e.target.value = "";
    if (files.length > 0) onSelect(files);
  }

  return (
    <div className={className}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" id={id} variant={variant} size={size}>
            <ImagePlus className="h-4 w-4" />
            {label ?? (multiple ? "Add photos" : "Add photo")}
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onSelect={() => cameraRef.current?.click()}>
            <Camera />
            Camera
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => photosRef.current?.click()}>
            <Image />
            Photos
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => filesRef.current?.click()}>
            <FolderOpen />
            Files
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
        onChange={handleChange}
      />
      <input
        ref={photosRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
        onChange={handleChange}
      />
      <input
        ref={filesRef}
        type="file"
        multiple={multiple}
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
        onChange={handleChange}
      />
    </div>
  );
}
